import { formatPhoneBalance } from '../../utils/formatters';
import { formatDate } from '../../utils/intlFormatters';
import { isHolding, isLoan, formatUnits, formatPrice, inDisplay } from '../../utils/subAccounts';
import { balanceRateSources, holdsForeign } from '../../utils/rateSources';
import { holdingReturnNote } from '../../utils/holdingReturns';

/**
 * What an account row shows for a holding, a loan or a person with loans, in
 * place of its plain balance, shared by the desktop and phone lists:
 *
 *   subtitle  line 2: "2.5 units · avg 125.00", "Sep 17, 2026 · lent … · received …",
 *             "2 open loans"
 *   lines     the figures on the right, one per currency: a holding's market
 *             value (its cost while it has no price) with its unrealized %, a
 *             loan's balance still owed with its annual return, what a person
 *             owes now
 *
 * Null for any other account, which keeps its balance as today. With
 * `convert` (the page's display currency), the figures become one line in it,
 * and a holding's % is its return in that currency when `convert.returns` has
 * it: each buy at its own day's rate, with its own-currency return in `pctNote`.
 */
export function subAccountView(account, t, convert = null) {
  const view = nativeView(account, t);
  if (!view || !convert) return view;
  const lines = inPageCurrency(view.lines, convert);
  const ret = isHolding(account) ? convert.returns?.get(account.id) : null;
  if (ret?.pct != null) lines[0] = { ...lines[0], pct: ret.pct, pctNote: holdingReturnNote(ret, t) };
  return { ...view, lines };
}

// The figures in the display currency, summed into one line; a currency with
// no rate is left out. The % stays when there was one line to carry it.
// MI-329: `isConverted` says whether any figure was in another currency (one
// that was not is exact, with no "≈"), and `rateSources` the rates it read.
function inPageCurrency(lines, convert) {
  const values = lines.map((l) => inDisplay(convert, l.currency, l.value)).filter((v) => v != null);
  return [{
    ...(lines.length === 1 ? lines[0] : { pct: null }),
    currency: convert.target,
    value: values.reduce((s, v) => s + v, 0),
    isConverted: holdsForeign(lines.map((l) => l.currency), convert.target),
    rateSources: balanceRateSources(lines.map((l) => l.currency), convert.target, convert.sources),
  }];
}

function nativeView(account, t) {
  const p = account?.position;
  if (isHolding(account) && p) {
    const priced = p.market_value != null;
    return {
      subtitle: p.average_cost != null
        ? t('accounts:row.holdingUnits', { count: Number(p.units) || 0, units: formatUnits(p.units), avg: formatPrice(p.average_cost) })
        : t('accounts:row.holdingUnitsOnly', { count: Number(p.units) || 0, units: formatUnits(p.units) }),
      lines: [{ currency: p.currency, value: priced ? p.market_value : p.cost, pct: priced ? p.unrealized_pct : null }],
    };
  }
  const l = account?.loan;
  if (isLoan(account) && l) {
    const lentReceived = t('accounts:row.loanLentReceived', {
      lent: formatPhoneBalance(l.currency, l.lent),
      received: formatPhoneBalance(l.currency, l.received),
    });
    return {
      subtitle: l.first_date ? `${formatDate(l.first_date)} · ${lentReceived}` : lentReceived,
      lines: [{
        currency: l.currency,
        value: l.owed,
        pct: l.annual_effective_pct,
        pctSuffix: ` ${t('accounts:balanceChart.annualEffective')}`,
      }],
    };
  }
  const summaries = account?.loans_summary;
  if (summaries?.length) {
    const open = summaries.reduce((s, r) => s + (r.open_loans || 0), 0);
    return {
      subtitle: t('accounts:row.openLoans', { count: open }),
      lines: summaries.map((r) => ({ currency: r.currency, value: r.owes_now, pct: null })),
    };
  }
  return null;
}

/**
 * A broker's "holdings USD 1,234.56", from its holdings totals; null without
 * holdings. With `convert`, one amount in the display currency, "≈" when some
 * holding was in another currency (MI-329).
 */
/**
 * A broker's return on its holdings, as Copilot's brokerage row shows it, in
 * place of the change in its cash: in the display currency from the page's
 * returns (each buy at its own day's rate), else its own since purchase when
 * it holds one currency. Null without holdings, or while the converted one is
 * still on its way.
 */
export function brokerReturn(account, t, convert = null) {
  const rows = account?.holdings_summary;
  if (!rows?.length) return null;
  const ret = convert?.brokerReturns?.get(account.id);
  if (ret?.pct != null) return { pct: ret.pct, note: holdingReturnNote(ret, t) };
  if (convert || rows.length !== 1 || !(rows[0].cost > 0)) return null;
  return { pct: (rows[0].unrealized_gain / rows[0].cost) * 100, note: null };
}

export function holdingsLine(account, t, convert = null) {
  const rows = account?.holdings_summary;
  if (!rows?.length) return null;
  const approx = convert && holdsForeign(rows.map((r) => r.currency), convert.target) ? '≈ ' : '';
  const amount = convert
    ? `${approx}${formatPhoneBalance(convert.target, rows.reduce((s, r) => s + (inDisplay(convert, r.currency, r.market_value) ?? 0), 0))}`
    : rows.map((r) => formatPhoneBalance(r.currency, r.market_value)).join(' · ');
  return t('accounts:row.holdingsTotal', { amount });
}
